import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

import {
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';
import DoApiService from '../../../services/DoApiService';

const MOMCompository = () => {

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [momList, setMomList] = useState([]);

  // Filters
  const [acType, setAcType] = useState('0');
  const [month, setMonth] = useState('0');
  const [year, setYear] = useState('0');

  const loadData = async () => {

    try {

      setLoading(true);
      setError(null);

      const userData = JSON.parse(localStorage.getItem('userData') || '{}');

      const response = await DoApiService.getMomCompositoryList({
        orgId: userData.orgId,
        userTypeId: userData.userTypeId,
        acType,
        month,
        year
      });

      if (response.success) {

        setMomList(response.data || []);

      } else {

        throw new Error(response.message || 'Failed to load data');

      }

    } catch (err) {

      console.error(err);
      setError(err.message);

    } finally {

      setLoading(false);

    }
  };

  useEffect(() => {

    loadData();

  }, [acType, month, year]);

  const handleViewFile = (file) => {

    if (!file) return;

    const fileUrl = `https://nabl7t.s3.ap-south-1.amazonaws.com/live/${file}`;

    window.open(fileUrl, '_blank');

  };

  const exportToExcel = () => {

    const excelData = momList.map((item, index) => ({

      'Date': item.date || '-',
      'Date To': item.dateTo || '-',
      'AC Type': item.acType || '-',
      'Approval Date': item.approvalDate || '-',
      'MOM PDF': item.momFormat || '-',
      'MOM DOC': item.momFile || '-'

    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'MOM Composite List'
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });

    const data = new Blob(
      [excelBuffer],
      {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
      }
    );

    saveAs(data, 'MOM_Composite_List.xlsx');

  };

  return (
    <Box sx={{ p: 3 }}>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon color="primary" />
          <Typography variant="h4" fontWeight={600}>
            MOM Composite List
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2
          }}
        >
          <Button
            variant="contained"
            onClick={exportToExcel}
          >
            Export Excel
          </Button>

          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>

          <Grid container spacing={2}>

            {/* AC Type */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">

                <InputLabel>
                  Select AC Type
                </InputLabel>

                <Select
                  value={acType}
                  label="Select AC Type"
                  onChange={(e) => setAcType(e.target.value)}
                >

                  <MenuItem value="0">
                    All
                  </MenuItem>

                  <MenuItem value="CAM">
                    CAM
                  </MenuItem>

                  <MenuItem value="TCAM">
                    TCAM
                  </MenuItem>

                </Select>

              </FormControl>
            </Grid>

            {/* Month */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">

                <InputLabel>
                  Select Month
                </InputLabel>

                <Select
                  value={month}
                  label="Select Month"
                  onChange={(e) => setMonth(e.target.value)}
                >

                  <MenuItem value="0">All</MenuItem>

                  <MenuItem value="1">January</MenuItem>
                  <MenuItem value="2">February</MenuItem>
                  <MenuItem value="3">March</MenuItem>
                  <MenuItem value="4">April</MenuItem>
                  <MenuItem value="5">May</MenuItem>
                  <MenuItem value="6">June</MenuItem>
                  <MenuItem value="7">July</MenuItem>
                  <MenuItem value="8">August</MenuItem>
                  <MenuItem value="9">September</MenuItem>
                  <MenuItem value="10">October</MenuItem>
                  <MenuItem value="11">November</MenuItem>
                  <MenuItem value="12">December</MenuItem>

                </Select>

              </FormControl>
            </Grid>

            {/* Year */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">

                <InputLabel>
                  Select Year
                </InputLabel>

                <Select
                  value={year}
                  label="Select Year"
                  onChange={(e) => setYear(e.target.value)}
                >

                  <MenuItem value="0">
                    All
                  </MenuItem>

                  <MenuItem value="2026">
                    2026
                  </MenuItem>

                  <MenuItem value="2025">
                    2025
                  </MenuItem>

                  <MenuItem value="2024">
                    2024
                  </MenuItem>

                </Select>

              </FormControl>
            </Grid>

          </Grid>

        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 300
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!loading && (
        <Card>
          <CardContent>

            <TableContainer component={Paper}>
              <Table>

                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>

                    <TableCell sx={{ fontWeight: 700 }}>
                      #
                    </TableCell>

                    <TableCell sx={{ fontWeight: 700 }}>
                      Date
                    </TableCell>

                    <TableCell sx={{ fontWeight: 700 }}>
                      Date To
                    </TableCell>

                    <TableCell sx={{ fontWeight: 700 }}>
                      AC Type
                    </TableCell>

                    <TableCell sx={{ fontWeight: 700 }}>
                      Approval Date
                    </TableCell>

                    <TableCell sx={{ fontWeight: 700 }}>
                      MOM PDF
                    </TableCell>

                    <TableCell sx={{ fontWeight: 700 }}>
                      MOM DOC
                    </TableCell>

                  </TableRow>
                </TableHead>

                <TableBody>

                  {momList.length > 0 ? (

                    momList.map((item, index) => (

                      <TableRow
                        key={index}
                        hover
                      >

                        <TableCell>
                          {index + 1}
                        </TableCell>

                        <TableCell>
                          {item.date || '-'}
                        </TableCell>

                        <TableCell>
                          {item.dateTo || '-'}
                        </TableCell>

                        <TableCell>
                          {item.acType || '-'}
                        </TableCell>

                        <TableCell>
                          {item.approvalDate || '-'}
                        </TableCell>

                        <TableCell>

                          {item.momFormat ? (

                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => handleViewFile(item.momFormat)}
                            >
                              View
                            </Button>

                          ) : '-'}

                        </TableCell>

                        <TableCell>

                          {item.momFile ? (

                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={() => handleViewFile(item.momFile)}
                            >
                              Download
                            </Button>

                          ) : '-'}

                        </TableCell>

                      </TableRow>

                    ))

                  ) : (

                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                      >
                        No Data Found
                      </TableCell>
                    </TableRow>

                  )}

                </TableBody>

              </Table>
            </TableContainer>

          </CardContent>
        </Card>
      )}

    </Box>
  );
};

export default MOMCompository;